import { useFormikContext } from 'formik';
import { Loader2 } from 'lucide-react';

import { ErrorField } from '@/components/error-field';
import { RequiredLabel } from '@/components/required-label';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

import type { AgentCreateFormValues } from '@/schemas/agent.schema';

import { useFormikError } from '@/lib/error';

import { Voices } from './voices';

type Props = {
  handleBack: () => void;
  isPending: boolean;
};

export const CreateAgentForm = ({ handleBack, isPending }: Props) => {
  const { getFieldProps, isValid } = useFormikContext<AgentCreateFormValues>();
  const hasError = useFormikError();

  return (
    <>
      <div className="flex flex-col gap-1.5 w-full">
        <div className="flex items-center w-full">
          <p className="font-semibold text-xl leading-7 text-foreground">Complete your agent</p>
        </div>
        <div className="flex items-center w-full">
          <p className="font-normal text-sm leading-5 text-muted-foreground">
            Give your agent a name, define its purpose, and optionally include a website.
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-4 w-full mt-4">
        <div className="flex flex-col gap-2 w-full">
          <Field aria-invalid={hasError('agent_name')}>
            <RequiredLabel htmlFor="agent-name">Agent name</RequiredLabel>
            <Input
              id="agent-name"
              aria-invalid={hasError('agent_name')}
              maxLength={100}
              {...getFieldProps('agent_name')}
              placeholder="Enter agent name"
            />
            <ErrorField name="agent_name" />
          </Field>
        </div>

        <Voices />

        <div className="flex flex-col gap-2 w-full">
          <Field aria-invalid={hasError('description')}>
            <RequiredLabel htmlFor="description">Description</RequiredLabel>
            <Textarea
              id="description"
              placeholder="Describe what you want your agent to accomplish..."
              className="min-h-[80px] resize-none"
              maxLength={500}
              {...getFieldProps('description')}
            />
            <ErrorField name="description" />
          </Field>
        </div>
      </div>

      <div className="flex items-start justify-between w-full mt-4">
        <Button variant="outline" onClick={handleBack} disabled={isPending}>
          Back
        </Button>
        <Button disabled={!isValid || isPending} type="submit">
          {isPending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Creating agent...
            </>
          ) : (
            'Create agent'
          )}
        </Button>
      </div>
    </>
  );
};
